import { shallowRef } from 'vue';

/** One entry of Drive's format catalog (`GET /v1/formats`): facts, nothing else. */
export interface DriveFormat {
  key: string;
  extensions: readonly string[];
  mediaType: string;
}

/** What the shell's app registry says about opening files. */
export interface DriveFormatApp {
  id: string;
  basePath: string;
  formatKeys?: readonly string[];
}

/** After a failed load, the next attempt waits this long. */
const RETRY_AFTER_MS = 30_000;

type CatalogState = 'loading' | 'ready' | 'failed';

const state = shallowRef<CatalogState>('loading');
const byExtension = shallowRef(new Map<string, DriveFormat>());
const appByFormat = shallowRef(new Map<string, DriveFormatApp>());
let pending: Promise<void> | null = null;
let lastFailure = 0;
let settleWaiters: Array<() => void> = [];

function settle(next: CatalogState) {
  state.value = next;
  const waiters = settleWaiters;
  settleWaiters = [];
  for (const resolve of waiters) resolve();
}

/** Takes over the catalog. The shell gets it from Drive, a test brings its own. */
export function setDriveFormats(formats: readonly DriveFormat[]): void {
  const index = new Map<string, DriveFormat>();
  for (const format of formats) {
    for (const extension of format.extensions) index.set(extension, format);
  }
  byExtension.value = index;
  settle('ready');
}

/** Takes over which app opens which format. The first app naming a format wins. */
export function setDriveFormatApps(apps: readonly DriveFormatApp[]): void {
  const index = new Map<string, DriveFormatApp>();
  for (const app of apps) {
    for (const key of app.formatKeys ?? []) {
      if (!index.has(key)) index.set(key, app);
    }
  }
  appByFormat.value = index;
}

/**
 * Loads the catalog once. Safe to call on every navigation: a loaded catalog
 * stays and a failed load is tried again after a while. Until then every format
 * counts as unknown, which means download.
 */
export function loadDriveFormats(
  fetchFormats: () => Promise<readonly DriveFormat[]>,
): Promise<void> {
  if (pending) return pending;
  if (state.value === 'ready') return Promise.resolve();
  if (state.value === 'failed' && Date.now() - lastFailure < RETRY_AFTER_MS) {
    return Promise.resolve();
  }
  // A retry is "not yet" again: whoever asks now waits for its answer.
  state.value = 'loading';
  pending = fetchFormats()
    .then(setDriveFormats, () => {
      lastFailure = Date.now();
      settle('failed');
    })
    .finally(() => {
      pending = null;
    });
  return pending;
}

/** False while the catalog is still on its way: "not yet", not "unsupported". */
export function driveFormatsSettled(): boolean {
  return state.value !== 'loading';
}

/** Resolves once `driveFormatsSettled` holds. */
export function whenDriveFormatsSettled(): Promise<void> {
  if (driveFormatsSettled()) return Promise.resolve();
  return new Promise((resolve) => settleWaiters.push(resolve));
}

/** The format a file name points at. A name is only a hint, never proof. */
export function driveFormat(name: string): DriveFormat | null {
  const base = name.slice(name.lastIndexOf('/') + 1);
  const dot = base.lastIndexOf('.');
  if (dot < 0) return null;
  return byExtension.value.get(base.slice(dot + 1).toLowerCase()) ?? null;
}

/** The app that opens a file, null when none does: the file is downloaded. */
export function driveFormatApp(name: string): DriveFormatApp | null {
  const format = driveFormat(name);
  return format ? (appByFormat.value.get(format.key) ?? null) : null;
}
